import React, { useEffect, useMemo, useState } from 'react';
import { Calculator, Wallet } from 'lucide-react';
import { toast } from 'react-hot-toast';
import useExchangeStore from '../../store/useExchangeStore';
import { formatCurrency } from '../../utils/formatters';
import { CURRENCY_ORDER, normalizeCurrencyCode } from '../../constants/currencies';
import { getFinanceRowTime, withChronologicalRunningBalance } from '../../utils/financeLedgerSort';
import LedgerToolbar from '../common/LedgerToolbar';
import { exportOperationProfitabilityPDF } from '../../utils/pdfGenerator';

const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;

const formatRate = (value) => (
  new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4
  }).format(Number(value || 0))
);

const getRateToTry = (currency, rates = {}, sourceRateOverride = null) => {
  const normalized = normalizeCurrencyCode(currency);
  if (normalized === 'TRY') return 1;
  const rate = Number(sourceRateOverride || rates?.[normalized] || 0);
  return rate > 0 ? rate : 1;
};

const convertAmount = (amount, sourceCurrency, targetCurrency, rates = {}, sourceRateOverride = null) => {
  const source = normalizeCurrencyCode(sourceCurrency);
  const target = normalizeCurrencyCode(targetCurrency);
  const numericAmount = Number(amount || 0);

  if (source === target) return numericAmount;

  const sourceRate = getRateToTry(source, rates, sourceRateOverride);
  const targetRate = getRateToTry(target, rates);

  if (target === 'TRY') return numericAmount * sourceRate;
  if (source === 'TRY') return numericAmount / targetRate;
  return (numericAmount * sourceRate) / targetRate;
};

const getRowDate = (row) => new Date(getFinanceRowTime(row));

const getRowLabel = (row) => (
  row?.invoiceNumber ||
  row?.transactionNumber ||
  row?.documentNo ||
  row?.description ||
  row?.accountLabel ||
  row?.typeLabel ||
  '-'
);

const getRowSourceCurrency = (row) => normalizeCurrencyCode(row?.currency || row?.financialAccount?.currency || 'TRY');

const getSourceRateOverride = (row, sourceCurrency, rates) => {
  if (sourceCurrency === 'TRY') return 1;

  const directRate = Number(
    row?.exchangeRate ||
    row?.invoiceExchangeRate ||
    row?.allocationExchangeRate ||
    row?.allocations?.[0]?.exchangeRate ||
    0
  );

  return directRate > 0 ? directRate : getRateToTry(sourceCurrency, rates);
};

const getInvoiceDirection = (type = '') => {
  const normalized = String(type || '').trim();
  if (normalized === 'Purchase' || normalized === 'SalesReturn') return 'expense';
  return 'income';
};

const getInvoiceTypeLabel = (type = '') => ({
  Sales: 'Gelir Faturası',
  Purchase: 'Gider Faturası',
  SalesReturn: 'Gelir İade Faturası',
  PurchaseReturn: 'Gider İade Faturası'
}[type] || type || '-');

const PERSONNEL_PAYMENT_CATEGORIES = new Set([
  'personnel_payment',
  'PersonnelPayment',
  'Personnel Payment',
  'Personel Odemesi',
  'Personel Ödemesi',
  'Maaş/Yevmiye',
  'Maas/Yevmiye',
  'Salary',
  'Wage'
]);

const getTransactionDirection = (row) => {
  if (String(row?.direction || '').toLowerCase() === 'outflow') return 'expense';
  if (String(row?.direction || '').toLowerCase() === 'inflow') return 'income';
  return String(row?.type || '').toLowerCase() === 'outflow' ? 'expense' : 'income';
};

const isExcludedPersonnelCashOutflow = (row) => {
  if (getTransactionDirection(row) !== 'expense') return false;
  return PERSONNEL_PAYMENT_CATEGORIES.has(String(row?.category || '').trim());
};

const toDateInputValue = (value) => {
  const date = value instanceof Date ? value : new Date(value || 0);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isInDateRange = (value, startDate, endDate) => {
  const time = value instanceof Date ? value.getTime() : new Date(value || 0).getTime();
  if (!Number.isFinite(time)) return true;
  if (startDate) {
    const start = new Date(`${startDate}T00:00:00`);
    if (time < start.getTime()) return false;
  }
  if (endDate) {
    const end = new Date(`${endDate}T23:59:59.999`);
    if (time > end.getTime()) return false;
  }
  return true;
};

const buildRows = (summary, reportCurrency, rates, sortDirection = 'desc', dateRange = {}) => {
  const report = normalizeCurrencyCode(reportCurrency);
  const invoiceRows = Array.isArray(summary?.invoiceRows) ? summary.invoiceRows : [];
  const transactionRows = (Array.isArray(summary?.transactionRows) ? summary.transactionRows : [])
    .filter((row) => !isExcludedPersonnelCashOutflow(row));

  const rows = [];

  invoiceRows.forEach((row) => {
    const sourceCurrency = getRowSourceCurrency(row);
    const sourceRate = getSourceRateOverride(row, sourceCurrency, rates);
    const originalAmount = Number(row?.grandTotal || 0);
    const direction = getInvoiceDirection(row?.type);
    const signedSourceAmount = direction === 'expense' ? -originalAmount : originalAmount;
    const convertedAmount = convertAmount(originalAmount, sourceCurrency, report, rates, sourceRate);
    const signedConvertedAmount = direction === 'expense' ? -convertedAmount : convertedAmount;
    const reportRate = sourceCurrency === report
      ? 1
      : getRateToTry(sourceCurrency, rates, sourceRate) / getRateToTry(report, rates);

    rows.push({
      kind: 'invoice',
      raw: row,
      date: getRowDate(row),
      sourceCurrency,
      reportCurrency: report,
      typeLabel: getInvoiceTypeLabel(row?.type),
      flowLabel: direction === 'expense' ? 'Gider' : 'Gelir',
      documentLabel: getRowLabel(row),
      originalAmount: signedSourceAmount,
      convertedAmount: signedConvertedAmount,
      exchangeRate: reportRate,
      originalAmountText: sourceCurrency === report
        ? formatCurrency(Math.abs(originalAmount), sourceCurrency)
        : `${formatCurrency(Math.abs(originalAmount), sourceCurrency)} @ ${formatRate(reportRate)}`
    });
  });

  transactionRows.forEach((row) => {
    const sourceCurrency = getRowSourceCurrency(row);
    const sourceRate = getSourceRateOverride(row, sourceCurrency, rates);
    const originalAmount = Number(row?.amount || 0);
    const direction = getTransactionDirection(row);
    const signedSourceAmount = direction === 'expense' ? -originalAmount : originalAmount;
    const convertedAmount = convertAmount(originalAmount, sourceCurrency, report, rates, sourceRate);
    const signedConvertedAmount = direction === 'expense' ? -convertedAmount : convertedAmount;
    const reportRate = sourceCurrency === report
      ? 1
      : getRateToTry(sourceCurrency, rates, sourceRate) / getRateToTry(report, rates);

    rows.push({
      kind: 'transaction',
      raw: row,
      date: getRowDate(row),
      sourceCurrency,
      reportCurrency: report,
      typeLabel: row?.directionLabel || row?.typeLabel || (direction === 'expense' ? 'Ödeme' : 'Tahsilat'),
      flowLabel: direction === 'expense' ? 'Gider' : 'Gelir',
      documentLabel: getRowLabel(row),
      originalAmount: signedSourceAmount,
      convertedAmount: signedConvertedAmount,
      exchangeRate: reportRate,
      originalAmountText: sourceCurrency === report
        ? formatCurrency(Math.abs(originalAmount), sourceCurrency)
        : `${formatCurrency(Math.abs(originalAmount), sourceCurrency)} @ ${formatRate(reportRate)}`
    });
  });

  const personnelRows = Array.isArray(summary?.personnelExpenseRows) && summary.personnelExpenseRows.length > 0
    ? summary.personnelExpenseRows
    : Number(summary?.personnelCost || summary?.personnel?.totalPersonnelCost || 0) > 0
      ? [{
          _id: 'personnel-cost-total',
          date: summary?.operation?.createdAt,
          createdAt: summary?.operation?.createdAt,
          currency: summary?.personnel?.currency || 'TRY',
          totalAmount: Number(summary?.personnelCost || summary?.personnel?.totalPersonnelCost || 0),
          description: 'Personel Hakediş Maliyeti',
          typeLabel: 'Personel Hakediş Maliyeti'
        }]
      : [];

  personnelRows.forEach((row) => {
    const sourceCurrency = getRowSourceCurrency(row);
    const originalAmount = Number(row?.totalAmount || 0);
    const convertedAmount = convertAmount(originalAmount, sourceCurrency, report, rates, Number(row?.exchangeRate || 0) || null);
    const reportRate = sourceCurrency === report
      ? 1
      : getRateToTry(sourceCurrency, rates, Number(row?.exchangeRate || 0) || null) / getRateToTry(report, rates);
    const documentLabel = row?.description || row?.accountLabel || 'Personel Hakediş Maliyeti';

    rows.push({
      kind: 'personnel',
      raw: row,
      date: getRowDate(row),
      sourceCurrency,
      reportCurrency: report,
      typeLabel: row?.typeLabel || 'Personel Hakediş Maliyeti',
      flowLabel: 'Gider',
      documentLabel,
      originalAmount: -originalAmount,
      convertedAmount: -convertedAmount,
      exchangeRate: reportRate,
      originalAmountText: sourceCurrency === report
        ? formatCurrency(Math.abs(originalAmount), sourceCurrency)
        : `${formatCurrency(Math.abs(originalAmount), sourceCurrency)} @ ${formatRate(reportRate)}`
    });
  });

  const materialRows = Array.isArray(summary?.materialConsumptionRows) ? summary.materialConsumptionRows : [];
  materialRows.forEach((row) => {
    const sourceCurrency = getRowSourceCurrency(row);
    const originalAmount = Number(row?.totalAmount || 0);
    const convertedAmount = convertAmount(originalAmount, sourceCurrency, report, rates);
    const reportRate = sourceCurrency === report
      ? 1
      : getRateToTry(sourceCurrency, rates) / getRateToTry(report, rates);
    const itemName = row?.inventoryItemId?.name || 'Sarf malzemesi';

    rows.push({
      kind: 'material',
      raw: row,
      date: getRowDate(row),
      sourceCurrency,
      reportCurrency: report,
      typeLabel: 'Malzeme / Sarf',
      flowLabel: 'Gider',
      documentLabel: itemName,
      originalAmount: -originalAmount,
      convertedAmount: -convertedAmount,
      exchangeRate: reportRate,
      originalAmountText: sourceCurrency === report
        ? formatCurrency(Math.abs(originalAmount), sourceCurrency)
        : `${formatCurrency(Math.abs(originalAmount), sourceCurrency)} @ ${formatRate(reportRate)}`
    });
  });

  const sortedRows = withChronologicalRunningBalance(
    rows.filter((row) => isInDateRange(row.date, dateRange.startDate, dateRange.endDate)),
    sortDirection
  );

  const totals = sortedRows.reduce((acc, row) => {
    if (row.convertedAmount >= 0) {
      acc.totalRevenue += row.convertedAmount;
    } else {
      acc.totalExpense += Math.abs(row.convertedAmount);
    }

    return acc;
  }, {
    totalRevenue: 0,
    totalExpense: 0
  });

  const netProfit = totals.totalRevenue - totals.totalExpense;
  const profitMargin = totals.totalRevenue > 0 ? (netProfit / totals.totalRevenue) * 100 : 0;

  return {
    reportCurrency: report,
    rows: sortedRows,
    totalRevenue: totals.totalRevenue,
    totalExpense: totals.totalExpense,
    netProfit,
    profitMargin
  };
};

const OperationFinanceTab = ({
  summary = null,
  loading = false,
  onOpenInvoice = null
}) => {
  const rates = useExchangeStore((state) => state.rates);
  const fetchRates = useExchangeStore((state) => state.fetchRates);
  const [reportCurrency, setReportCurrency] = useState('TRY');
  const [sortDirection, setSortDirection] = useState('desc');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [pdfLanguage, setPdfLanguage] = useState('tr');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  const dateBounds = useMemo(() => {
    const dates = [
      ...(summary?.invoiceRows || []),
      ...(summary?.transactionRows || []),
      ...(summary?.personnelExpenseRows || []),
      ...(summary?.materialConsumptionRows || [])
    ]
      .map((row) => new Date(row?.date || row?.createdAt || 0))
      .filter((date) => !Number.isNaN(date.getTime()))
      .sort((left, right) => left.getTime() - right.getTime());

    return {
      startDate: dates[0] ? toDateInputValue(dates[0]) : toDateInputValue(new Date()),
      endDate: toDateInputValue(new Date())
    };
  }, [summary]);

  const resolvedStartDate = startDate || dateBounds.startDate;
  const resolvedEndDate = endDate || dateBounds.endDate;

  const ledger = useMemo(
    () => buildRows(summary, reportCurrency, rates, sortDirection, {
      startDate: resolvedStartDate,
      endDate: resolvedEndDate
    }),
    [reportCurrency, rates, resolvedEndDate, resolvedStartDate, sortDirection, summary]
  );
  const materialCost = useMemo(() => (
    (Array.isArray(summary?.materialConsumptionRows) ? summary.materialConsumptionRows : [])
      .reduce((sum, row) => sum + convertAmount(Number(row?.totalAmount || 0), getRowSourceCurrency(row), reportCurrency, rates), 0)
  ), [rates, reportCurrency, summary]);

  if (loading && !summary) {
    return (
      <div className="rounded-[2rem] border border-slate-100 bg-white p-10 text-center font-bold text-slate-400 shadow-sm">
        Finans özeti yükleniyor...
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="rounded-[2rem] border border-dashed border-slate-200 bg-slate-50 p-10 text-center font-bold text-slate-400">
        Bu operasyon için finans özeti bulunamadı.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-[2rem] border border-slate-100 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex items-center gap-2">
            <Calculator size={18} className="text-blue-600" />
            <div>
              <h2 className="text-lg font-black text-slate-800">Finans Raporu</h2>
              <p className="text-xs font-bold text-slate-400">Seçilen para birimine göre tüm fatura ve giderler yeniden hesaplanır.</p>
            </div>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <label className="flex flex-col gap-1">
              <span className="ml-1 text-[10px] font-black uppercase tracking-widest text-slate-400">Raporlama Para Birimi</span>
              <select
                value={reportCurrency}
                onChange={(e) => setReportCurrency(e.target.value)}
                className="min-w-[180px] rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-black text-slate-700 outline-none focus:border-blue-200 focus:bg-white focus:ring-4 focus:ring-blue-50"
              >
                {CURRENCY_ORDER.map((currency) => (
                  <option key={currency} value={currency}>{currency}</option>
                ))}
              </select>
            </label>

          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        <div className="rounded-[2rem] border border-emerald-100 bg-emerald-50/80 p-5">
          <p className="text-[10px] font-black uppercase tracking-widest text-emerald-500">Toplam Gelir</p>
          <p className="mt-2 text-xl font-black text-emerald-700">{formatCurrency(ledger.totalRevenue, ledger.reportCurrency)}</p>
        </div>
        <div className="rounded-[2rem] border border-rose-100 bg-rose-50/80 p-5">
          <p className="text-[10px] font-black uppercase tracking-widest text-rose-500">Toplam Gider</p>
          <p className="mt-2 text-xl font-black text-rose-700">{formatCurrency(ledger.totalExpense, ledger.reportCurrency)}</p>
        </div>
        <div className="rounded-[2rem] border border-amber-100 bg-amber-50/80 p-5">
          <p className="text-[10px] font-black uppercase tracking-widest text-amber-600">Toplam Malzeme Maliyeti</p>
          <p className="mt-2 text-xl font-black text-amber-800">{formatCurrency(materialCost, ledger.reportCurrency)}</p>
        </div>
        <div className={`rounded-[2rem] border p-5 ${ledger.netProfit >= 0 ? 'border-emerald-100 bg-emerald-50/80' : 'border-rose-100 bg-rose-50/80'}`}>
          <p className={`text-[10px] font-black uppercase tracking-widest ${ledger.netProfit >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>Net Kâr / Zarar</p>
          <p className={`mt-2 text-xl font-black ${ledger.netProfit >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
            {formatCurrency(ledger.netProfit, ledger.reportCurrency)}
          </p>
        </div>
        <div className="rounded-[2rem] border border-slate-100 bg-white p-5 shadow-sm">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Kâr Marjı</p>
          <p className={`mt-2 text-xl font-black ${ledger.profitMargin >= 0 ? 'text-slate-900' : 'text-rose-600'}`}>
            {formatPercent(ledger.profitMargin)}
          </p>
        </div>
      </div>

      <div className="overflow-hidden rounded-[2rem] border border-slate-100 bg-white shadow-sm">
        <div className="flex items-end justify-between gap-4 px-5 pt-5">
          <div className="flex items-center gap-2">
            <Wallet size={18} className="text-emerald-600" />
            <div>
              <h2 className="text-lg font-black text-slate-800">Hareketler</h2>
              <p className="text-xs font-bold text-slate-400">Akış, raporlama para birimine çevrilmiş değerlerle gösterilir.</p>
            </div>
          </div>
        </div>
        <LedgerToolbar
          sortDirection={sortDirection}
          onSortDirectionChange={setSortDirection}
          startDate={resolvedStartDate}
          onStartDateChange={setStartDate}
          endDate={resolvedEndDate}
          onEndDateChange={setEndDate}
          pdfLanguage={pdfLanguage}
          onPdfLanguageChange={setPdfLanguage}
          exporting={exporting}
          onExport={async () => {
            if (exporting) return;
            setExporting(true);
            try {
              exportOperationProfitabilityPDF(
                summary?.operation || {},
                ledger,
                pdfLanguage,
                resolvedStartDate,
                resolvedEndDate
              );
            } catch (error) {
              toast.error(error?.message || 'Karlılık PDF indirilemedi.');
            } finally {
              setExporting(false);
            }
          }}
        />

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-400">
                <th className="p-4">Tarih</th>
                <th className="p-4">İşlem Türü</th>
                <th className="p-4">Belge No / Açıklama</th>
                <th className="p-4 text-right">Orijinal Tutar</th>
                <th className="p-4 text-right">Çevrim Kuru</th>
                <th className="p-4 text-right">Tutar ({ledger.reportCurrency})</th>
                <th className="p-4 text-right">Yürüyen Karlılık Bakiyesi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {ledger.rows.map((row) => {
                const isInvoiceRow = row.kind === 'invoice';
                const rowClickable = isInvoiceRow && typeof onOpenInvoice === 'function';
                const positive = row.convertedAmount >= 0;

                return (
                  <tr
                    key={`${row.kind}-${row.raw?._id || row.documentLabel}-${row.date?.toISOString?.() || ''}`}
                    className={`text-sm transition-colors ${rowClickable ? 'cursor-pointer hover:bg-slate-50' : 'hover:bg-slate-50/60'}`}
                    onClick={rowClickable ? () => onOpenInvoice(row.raw) : undefined}
                  >
                    <td className="p-4 align-top font-black text-slate-800">
                      {Number.isNaN(row.date?.getTime?.()) ? '-' : row.date.toLocaleDateString('tr-TR')}
                    </td>
                    <td className="p-4 align-top">
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${positive ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                        {row.flowLabel}
                      </span>
                    </td>
                    <td className="p-4 align-top">
                      <div className="flex flex-col">
                        <span className="font-black text-slate-800">{row.documentLabel}</span>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                          {row.typeLabel}
                          {isInvoiceRow ? ' • Fatura' : ''}
                        </span>
                      </div>
                    </td>
                    <td className="p-4 align-top text-right font-black text-slate-800">
                      {row.originalAmountText}
                    </td>
                    <td className="p-4 align-top text-right font-black text-slate-500">
                      {row.sourceCurrency === ledger.reportCurrency ? '1.0000' : formatRate(row.exchangeRate)}
                    </td>
                    <td className={`p-4 align-top text-right font-black ${positive ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {formatCurrency(Math.abs(row.convertedAmount), ledger.reportCurrency)}
                    </td>
                    <td className={`p-4 align-top text-right font-black ${row.runningBalance >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {formatCurrency(row.runningBalance, ledger.reportCurrency)}
                    </td>
                  </tr>
                );
              })}

              {ledger.rows.length === 0 && (
                <tr>
                  <td colSpan="7" className="py-16 text-center text-slate-400 font-bold">
                    Bu operasyon için gösterilecek hareket bulunamadı.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OperationFinanceTab;
